/**
 * A writable project tree with a fixed base. The tree and linked directory
 * documents are cloned when the branch starts. File bodies stay at their
 * captured versions until a write promotes one to a private document.
 */
import * as Automerge from "@automerge/automerge";
import type { AutomergeUrl, DocHandle, Repo } from "@automerge/automerge-repo";
import { invariant } from "ts-invariant";

import { listedEntries, plainCreateDoc, type CreateDoc } from "../fs/dir-tree";
import { isMountInode } from "../fs/mount";
import { isInlineDir, isLinkedDir } from "../fs/paths";
import type {
  DirectoryDoc,
  FileVersion,
  PlayDirInode,
  PlayFileInode,
  PlayFileInodeV2,
} from "../fs/types";
import { automergeVersion, pinnedUrl } from "../fs/version";
import type { BlobStore } from "../hal/blobStore/index";
import { createPlayground } from "../hal/playground";
import type { CheckpointsRefDoc, Project } from "../project";

export interface BranchSource {
  uri: AutomergeUrl;
  heads: string[];
}

export interface ProjectBranchMeta {
  kind: "project-branch";
  source: BranchSource;
  createdAt: number;
  /** Original directory URL to its branch copy and captured heads. */
  directories: Record<string, BranchSource>;
  /** Original file URL to the exact body the branch began with. */
  baseFiles: Record<string, FileVersion>;
  /** Private file URL to its original document and captured heads. */
  files: Record<string, BranchSource>;
}

export type BranchedProject = Project & { branch: ProjectBranchMeta };

export function isBranchedProject(doc: Project): doc is BranchedProject {
  return (
    "branch" in doc &&
    typeof doc.branch === "object" &&
    doc.branch !== null &&
    (doc.branch as ProjectBranchMeta).kind === "project-branch"
  );
}

const plain = <T>(value: T): T => structuredClone(value);

async function ready<T>(repo: Repo, uri: AutomergeUrl): Promise<DocHandle<T>> {
  const handle = await repo.find<T>(uri);
  await handle.whenReady();
  invariant(handle.doc(), `Document not found: ${uri}`);
  return handle;
}

/**
 * Capture the branch's tree before publishing its root. A failed blob pin or
 * missing document leaves only unreachable clones, never a partial branch.
 */
export async function createProjectBranch(
  repo: Repo,
  sourceUrl: AutomergeUrl,
  blobs: BlobStore,
  createDoc: CreateDoc = plainCreateDoc(repo),
): Promise<AutomergeUrl> {
  const source = await ready<Project>(repo, sourceUrl);
  const sourceDoc = source.doc()!;
  const sourceHeads = source.heads();
  invariant(sourceHeads?.length, `Project has no heads: ${sourceUrl}`);

  const directories: Record<string, BranchSource> = {};
  const directoryCopies = new Map<string, AutomergeUrl>();
  const fileVersions = new Map<string, FileVersion>();
  const blobVersions = new Map<string, FileVersion>();

  const copyEntries = async (
    entries: Record<string, PlayFileInode>,
    ancestors: ReadonlySet<string>,
  ): Promise<Record<string, PlayFileInode>> => {
    const copied: Record<string, PlayFileInode> = {};
    for (const [name, inode] of Object.entries(listedEntries(entries))) {
      if (isMountInode(inode)) {
        copied[name] = plain(inode);
      } else if (isInlineDir(inode)) {
        const children = await copyEntries(inode.files.entries, ancestors);
        const directory: PlayDirInode = {
          ...plain(inode),
          files: { ...plain(inode.files), entries: children },
        };
        copied[name] = directory;
      } else if (isLinkedDir(inode)) {
        invariant(
          !ancestors.has(inode.uri),
          `Cannot branch a directory cycle at ${inode.uri}`,
        );
        let copiedUrl = directoryCopies.get(inode.uri);
        if (!copiedUrl) {
          const original = await ready<DirectoryDoc>(
            repo,
            inode.uri as AutomergeUrl,
          );
          const heads = original.heads();
          invariant(heads?.length, `Directory has no heads: ${inode.uri}`);
          const children = await copyEntries(
            original.doc()!.entries,
            new Set([...ancestors, inode.uri]),
          );
          const copy = await createDoc<DirectoryDoc>({} as DirectoryDoc);
          copy.update(() => Automerge.clone(original.doc()!));
          copy.change((doc) => {
            doc.entries = plain(children);
          });
          copiedUrl = copy.url;
          directoryCopies.set(inode.uri, copiedUrl);
          directories[inode.uri] = { uri: copiedUrl, heads: [...heads] };
        }
        copied[name] = { ...plain(inode), uri: copiedUrl };
      } else if (inode.uri.startsWith("automerge:")) {
        let version = inode.version ?? fileVersions.get(inode.uri);
        if (!version) {
          const file = await ready(repo, inode.uri as AutomergeUrl);
          const heads = file.heads();
          invariant(heads?.length, `File has no heads: ${inode.uri}`);
          version = automergeVersion(inode.uri as AutomergeUrl, heads);
        }
        invariant(
          version.kind === "automerge",
          `Invalid file version: ${inode.uri}`,
        );
        fileVersions.set(inode.uri, version);
        copied[name] = { ...plain(inode), version };
      } else {
        let version = inode.version ?? blobVersions.get(inode.uri);
        if (!version) {
          const pinned = await blobs.pin(inode.uri);
          version = { kind: "blob", blobId: pinned.blobId };
        }
        invariant(
          version.kind === "blob",
          `Invalid blob version: ${inode.uri}`,
        );
        blobVersions.set(inode.uri, version);
        copied[name] = {
          ...plain(inode),
          version,
        };
      }
    }
    return copied;
  };

  const files = await copyEntries(sourceDoc.files, new Set());
  let checkpointsDocUrl: AutomergeUrl | undefined;
  if (sourceDoc.checkpointsDocUrl) {
    const refs = await ready<CheckpointsRefDoc>(
      repo,
      sourceDoc.checkpointsDocUrl,
    );
    const copy = await createDoc<CheckpointsRefDoc>({} as CheckpointsRefDoc);
    copy.update(() => Automerge.clone(refs.doc()!));
    checkpointsDocUrl = copy.url;
  }
  const branch = await createDoc<BranchedProject>({} as BranchedProject);
  branch.update(
    () => Automerge.clone(sourceDoc) as Automerge.Doc<BranchedProject>,
  );
  branch.change((doc) => {
    doc.files = plain(files);
    if (checkpointsDocUrl) doc.checkpointsDocUrl = checkpointsDocUrl;
    doc.branch = {
      kind: "project-branch",
      source: { uri: sourceUrl, heads: [...sourceHeads] },
      createdAt: Date.now(),
      directories,
      baseFiles: Object.fromEntries([...fileVersions, ...blobVersions]),
      files: {},
    };
  });
  return branch.url;
}

/**
 * Promote one captured Automerge file to a private branch document. A second
 * call for the same file returns its current URI without making another copy.
 * The caller must use the returned URI for its write.
 */
export async function forkBranchFile(
  repo: Repo,
  branchUrl: AutomergeUrl,
  path: string,
  createDoc: CreateDoc = plainCreateDoc(repo),
): Promise<AutomergeUrl> {
  const root = await ready<BranchedProject>(repo, branchUrl);
  invariant(isBranchedProject(root.doc()!), `Not a branch: ${branchUrl}`);
  const playground = await createPlayground({
    repo,
    root: branchUrl,
    createDoc,
  });
  const found = await playground.resolve(path);
  invariant(found, `File not found: ${path}`);
  const { inode } = found;
  invariant(
    inode.uri.startsWith("automerge:"),
    `Not an Automerge file: ${path}`,
  );
  if (!inode.version) return inode.uri as AutomergeUrl;
  invariant(
    inode.version.kind === "automerge",
    `Automerge file has a blob version: ${path}`,
  );

  const source = await ready<Record<string, unknown>>(
    repo,
    pinnedUrl(inode.version),
  );
  const copy = await createDoc<Record<string, unknown>>({});
  copy.update(() => Automerge.clone(source.doc()!));
  const sourceRef = {
    uri: inode.uri as AutomergeUrl,
    heads: [...inode.version.heads],
  };
  const editable: PlayFileInodeV2 = {
    ...plain(inode),
    uri: copy.url,
    mtime: "mtime" in inode ? inode.mtime : 0,
    size: "size" in inode ? inode.size : 0,
  };
  delete editable.version;
  const linked = await playground.replaceFileIfCurrent(
    found.path,
    inode,
    editable,
  );
  invariant(linked.uri.startsWith("automerge:"), `File changed type: ${path}`);
  if (linked.uri !== copy.url) return linked.uri as AutomergeUrl;
  root.change((doc) => {
    doc.branch.files[copy.url] = sourceRef;
  });
  return copy.url;
}
